import type { Issue, IssueMode, IssueStatus } from '../types/issue';
import { isTransitionAllowed } from '../types/issue';
import { hasInvestigationFact } from './problemCause';

export interface ObservationCounts {
  strata: number;
  gemba: number;
  evidence: number;
}

export type ObservationFocus = 'phenomenon' | 'stratum' | 'gemba' | 'cause';

/** Próximo trabalho da observação. Estratificação e Gemba orientam a CTA; não travam a análise. */
export function observationWorkCta(
  phenomenon: boolean,
  counts: Pick<ObservationCounts, 'strata' | 'gemba'>,
): { label: string; focus: ObservationFocus } {
  if (!phenomenon) return { label: 'Descrever fenômeno', focus: 'phenomenon' };
  if (counts.strata === 0) return { label: 'Adicionar estratificação', focus: 'stratum' };
  if (counts.gemba === 0) return { label: 'Registrar observação', focus: 'gemba' };
  return { label: 'Iniciar análise de causa', focus: 'cause' };
}

export type IdentificationFocus = 'loss' | 'control' | 'phenomenon';

export function identificationWorkCta(input: { losses: number; hasControl: boolean }): {
  label: string;
  focus: IdentificationFocus;
} {
  if (input.losses === 0) return { label: 'Registrar perda', focus: 'loss' };
  if (!input.hasControl) return { label: 'Definir item de controle', focus: 'control' };
  return { label: 'Descrever fenômeno', focus: 'phenomenon' };
}

export interface CauseCheckItem {
  label: string;
  done: boolean;
  detail: string;
}

export type CauseConsultAction = 'describe' | 'begin-cause' | 'begin-observation' | null;

export function causeConsultation(
  issue: Pick<Issue, 'mode' | 'status' | 'root_cause_analysis'>,
  counts: Pick<ObservationCounts, 'strata' | 'gemba'>,
): { items: CauseCheckItem[]; note: string | null; action: CauseConsultAction } {
  const phenomenon = hasInvestigationFact(issue);
  const items: CauseCheckItem[] = [
    { label: 'Fenômeno observado', done: phenomenon, detail: '' },
    { label: 'Estratificação', done: counts.strata > 0, detail: String(counts.strata) },
    { label: 'Observações de Gemba', done: counts.gemba > 0, detail: String(counts.gemba) },
  ];
  if (!phenomenon) {
    return { items, note: 'Falta descrever o fenômeno observado.', action: 'describe' };
  }
  if (isTransitionAllowed(issue.mode, issue.status, 'root_cause')) {
    return { items, note: null, action: 'begin-cause' };
  }
  if (isTransitionAllowed(issue.mode as IssueMode, issue.status as IssueStatus, 'investigation')) {
    return { items, note: 'A análise abre depois da observação.', action: 'begin-observation' };
  }
  return { items, note: 'A análise de causa ainda não está disponível nesta etapa.', action: null };
}
